// Design tokens for the TrakLog redesign (design handoff "TrakLog UI.dc.html",
// options 3a / 4a-c / 5a-c).
//
// The mock is a flat industrial shell rather than Material's default: hairlines
// instead of elevation, mono for anything a supervisor scans (ids, timestamps,
// sizes, counts), and the template colour reduced to a 3px rail on the card.
// These are the literal values from the handoff — components reach for the
// token rather than re-typing an rgba(), so a palette change lands in one file.

export const TRAK = {
  // Brand
  //
  // `green` is the app bar, solid buttons and docked bars — and is the same
  // #137a3e the app used before the redesign. The redesign moved that chrome
  // onto `ink`, which reads as near-black rather than green and made the whole
  // shell heavier than it was.
  //
  // `ink` keeps its other job, which is the larger one: it is the near-black
  // green that headings and body text are set in, on light surfaces. Those
  // must not follow the chrome — 66 of its uses are text, and green-on-white
  // body copy is a different product.
  green: '#137a3e',        // app bar, solid buttons, docked bar
  greenDark: '#0d5c2d',    // their hover
  ink: '#0d3320',          // near-black text, and the tooltip ground
  accent: '#2ECC7A',

  // Text
  text: '#14201a',
  muted: 'rgba(13,51,32,.55)',
  faint: 'rgba(13,51,32,.4)',
  ghost: 'rgba(13,51,32,.32)',

  // Surfaces
  page: '#f6f8f6',
  surface: '#ffffff',
  fill: '#f2f4f2',
  fillSoft: '#fbfcfb',
  hairline: 'rgba(13,51,32,.10)',
  hairlineSoft: 'rgba(13,51,32,.07)',
  border: 'rgba(13,51,32,.18)',
  borderSoft: 'rgba(13,51,32,.16)',

  // Status
  successFill: '#e9f4ec',
  warn: '#b26a00',
  warnText: '#8a5a00',
  warnFill: '#fdf2dd',
  warnFillSoft: '#fdfaf3',
  warnDot: '#e0a02a',
  danger: '#c62828',
  dangerText: '#a3231f',
  dangerFill: '#fdeceb',
  dangerOnDark: '#ffb4b1',

  // Media viewer. Neutral rather than the brand ink: a full-screen green
  // ground casts a tint over every frame, and colour is one of the things a
  // reviewer is judging. The brand stays in the chrome (chips, Export button).
  viewerBg: '#111412',
  viewerStage: '#080908',
  viewerThumb: '#2b302d',
  viewerScrim: 'rgba(0,0,0,.68)',

  // On-dark (lightbox, app bar, docked bar)
  //
  // Lightened when the chrome moved from #0d3320 to the brand green. The bar
  // is a mid green now, so everything sitting on it lost contrast. The chip
  // label mattered most — it fell from 10.2:1 to 4.0:1, under AA at that size
  // — and is back to 5.0:1 against #137a3e.
  //
  // `onDarkMuted` reaches 4.0:1 rather than 4.5:1, and that is the deliberate
  // floor: it is secondary text whose whole job is to recede, and pushing it
  // to AA turns it white. 4.0:1 clears the 3:1 bar for large and secondary
  // text. White primary text on this green is 5.4:1, unchanged from the
  // pre-redesign app bar, which used the same colour.
  onDarkChip: 'rgba(255,255,255,.16)',
  onDarkChipText: '#eafaf0',
  onDarkBorder: 'rgba(255,255,255,.3)',
  onDarkMuted: 'rgba(255,255,255,.78)',
  onDarkFaint: 'rgba(255,255,255,.58)',

  // Type
  // IBM Plex Sans has no Thai glyphs, so templates and answers captured on a
  // Thai phone fell through to whatever system face the browser picked. IBM
  // Plex Sans Thai is the matching design and only covers Thai, so Latin text
  // still renders in IBM Plex Sans. The mono stack takes it as a glyph
  // fallback for Thai values in the collected-data table.
  sans: "'IBM Plex Sans', 'IBM Plex Sans Thai', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', 'IBM Plex Sans Thai', ui-monospace, Menlo, Consolas, monospace",

  // Shadows
  shadowCardHover: '0 2px 10px rgba(13,51,32,.07)',
  shadowMenu: '0 12px 32px rgba(13,51,32,.18)',
  shadowPopover: '0 14px 36px rgba(13,51,32,.18)',
  shadowBar: '0 10px 28px rgba(13,51,32,.28)',
  shadowSelected: '0 0 0 3px rgba(19,122,62,.10)',

  // The rail a task with no template gets.
  railNone: 'rgba(13,51,32,.16)',
};

// Uppercase micro-label used above every metadata value and popover section.
export const LABEL_SX = {
  font: `600 9.5px/1 ${TRAK.sans}`,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: TRAK.faint,
};

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function hexToRgb(hex) {
  if (!HEX_RE.test(hex)) return null;
  let body = hex.slice(1);
  if (body.length === 3) body = body.split('').map(c => c + c).join('');
  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

/**
 * Saturated rail colour for a template's pastel.
 *
 * Templates already carry a curated pastel (`TEMPLATE_PASTEL_COLORS`, MUI
 * 50/100 shades) chosen to sit *behind* text as a card wash. The redesign
 * spends that colour on a 3px rail and on the template name instead, and a
 * #E3F2FD rail is invisible against a white card — so the hue is kept and the
 * saturation/lightness are pushed to the mock's `#2b7bb9` / `#7a5bb9` register.
 *
 * Returns null for a missing or malformed colour; callers render `railNone`.
 */
export function railColor(pastelHex) {
  const rgb = hexToRgb(pastelHex || '');
  if (!rgb) return null;

  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  // A grey pastel (delta 0) has no hue worth saturating — keep it neutral.
  const s = delta === 0 ? 0.08 : 0.55;
  const l = delta === 0 ? 0.42 : 0.45;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const [r1, g1, b1] = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ][seg];

  const to255 = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to255(r1)}${to255(g1)}${to255(b1)}`;
}
